// Partner-facing words for the intake rejection codes the backend sends
// (leads/rejected.py). Moved here unchanged from the old LeadQuality widget.
// Deliberately not utils/rejections.js: that one is written for staff ("the
// partner has to..."), this one is read by the partner.
// Copy read by people outside the company, so it is written to be true on
// its own — a partner reads one bar, not the whole widget, and each line has
// to survive being read alone.
//
// Two of them are load-bearing and were checked against the backend rather
// than guessed:
//   - duplicate_*: leads/intake/dedup.py matches on phone/email across the
//     whole Lead table with no affiliate scope. "You already sent this" would
//     be a straight-up false accusation — the earlier row may be another
//     partner's. Hence "already in our system", plus the explicit note.
//   - daily_cap_reached: companies/affiliates/limits.py counts LEAD_RECEIVED
//     for the affiliate profile per day, with no campaign filter — it is an
//     account-wide cap we enforce, not a defect on their side and not
//     something scoped to one campaign.
const BLOCK_REASONS = {
  duplicate_email: {
    label: 'Duplicate email',
    tone: 'amber',
    detail: 'This email was already in our system within the last 24 hours. De-duplication is global across all sources, so the earlier match may not be yours.',
  },
  duplicate_phone: {
    label: 'Duplicate phone',
    tone: 'amber',
    detail: 'This phone number was already in our system within the last 24 hours. De-duplication is global across all sources, so the earlier match may not be yours.',
  },
  validation_failed: {
    label: 'Failed validation',
    tone: 'gray',
    detail: "The submission didn't pass our field checks — format, required fields, or plausibility.",
  },
  invalid_field: {
    label: 'Invalid field value',
    tone: 'gray',
    detail: "One or more fields carried a value this campaign can't accept.",
  },
  compliance_suppressed: {
    label: 'Suppressed for compliance',
    tone: 'red',
    detail: "The contact is on a suppression list, so we can't process the submission.",
  },
  consent_cert_missing: {
    label: 'No consent record',
    tone: 'red',
    detail: 'The submission arrived without the consent proof this campaign requires.',
  },
  daily_cap_reached: {
    label: 'Daily cap reached — we paused intake',
    tone: 'gray',
    detail: 'Nothing was wrong with these submissions. Your account reached its daily submission limit, which counts every campaign together, so we stopped accepting for the rest of the day. It clears on its own at midnight.',
  },
  affiliate_not_live: {
    label: 'Account not live',
    tone: 'gray',
    detail: "Your account wasn't set to live when this arrived, so intake was closed. Your account manager can switch it on.",
  },
  invalid_campaign: {
    label: 'Campaign not recognized',
    tone: 'gray',
    detail: "The campaign sent with the request doesn't match an active campaign on your account.",
  },
  invalid_test_output: {
    label: 'Test — invalid output',
    tone: 'gray',
    detail: "A test submission whose requested response didn't match a format we recognize. Test traffic only.",
  },
  test_simulated_error: {
    label: 'Test — simulated error',
    tone: 'gray',
    detail: 'An error deliberately triggered by a test submission. Not real traffic.',
  },
  test_simulated_rejection: {
    label: 'Test — simulated rejection',
    tone: 'gray',
    detail: 'A rejection deliberately triggered by a test submission. Not real traffic.',
  },
  unknown: {
    label: 'Not classified',
    tone: 'gray',
    detail: "We couldn't map these to a specific reason. If you see a lot of them, let your account manager know.",
  },
}

// Same severity language the rest of the app uses: red blocks revenue and
// needs someone to act, amber is expected noise, gray is everything else.
const TONE_COLOR = {
  red: 'var(--red)',
  amber: 'var(--amber)',
  gray: 'var(--text-muted)',
}

// A code the backend added after this shipped still gets a readable bar and
// its count, rather than being dropped — an unexplained bar beats a total
// that doesn't add up.
function humanizeReason(code) {
  const words = String(code || '').replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Unknown'
}

// Label, the visible one-line explanation, and the dot colour for a code.
export function partnerReason(code) {
  const meta = BLOCK_REASONS[code]
  return {
    label: meta ? meta.label : humanizeReason(code),
    detail: meta ? meta.detail : "We don't have a description for this reason yet. Your account manager can look it up.",
    color: TONE_COLOR[meta?.tone] || 'var(--text-muted)',
  }
}